/**
 * Route table and the two gates every screen sits behind: a session, and the
 * account list.
 *
 * `/auth/callback` is checked before the session gate — it is the screen whose
 * whole job is to create a session, so it must render while there is none.
 */

import { lazy, Suspense } from 'react';
import { AccountsProvider } from './lib/accounts-store';
import { SessionProvider, useSession } from './lib/session';
import { Loading } from './components/Feedback';
import { Shell } from './components/Shell';
import { Accounts } from './screens/Accounts';
import { AuthCallback } from './screens/AuthCallback';
import { CountryPicker } from './screens/CountryPicker';
import { FreeTv } from './screens/FreeTv';
import { Home } from './screens/Home';
import { Live } from './screens/Live';
import { MovieDetail } from './screens/MovieDetail';
import { Movies } from './screens/Movies';
import { People } from './screens/People';
import { Settings } from './screens/Settings';
import { Series } from './screens/Series';
import { SeriesDetail } from './screens/SeriesDetail';
import { SignIn } from './screens/SignIn';
import { Link, matchPath, useLocation } from './router/router';

/**
 * The player screen is loaded on demand: hls.js is by far the largest thing in
 * the bundle and nothing before /watch needs it, so the sign-in and browse
 * screens should not pay for it.
 */
const Watch = lazy(() => import('./screens/Watch').then((module) => ({ default: module.Watch })));
const UsaPlaylistTest = lazy(() =>
  import('./screens/UsaPlaylistTest').then((module) => ({ default: module.UsaPlaylistTest })),
);

export function App() {
  return (
    <SessionProvider>
      <Root />
    </SessionProvider>
  );
}

function Root() {
  const { status } = useSession();
  const { pathname } = useLocation();

  if (pathname === '/auth/callback') return <AuthCallback />;

  if (status === 'loading') {
    return (
      <div className="page">
        <Loading label="Starting up" />
      </div>
    );
  }

  if (status === 'anonymous') return <SignIn />;

  return (
    <AccountsProvider>
      <Routes />
    </AccountsProvider>
  );
}

function Routes() {
  const { pathname } = useLocation();

  // The player owns the whole viewport; the app chrome would only get in the
  // way of a full-bleed video.
  if (pathname === '/watch') {
    return (
      <Suspense
        fallback={
          <div className="page">
            <Loading label="Starting the player" />
          </div>
        }
      >
        <Watch />
      </Suspense>
    );
  }

  return <Shell>{renderScreen(pathname)}</Shell>;
}

function renderScreen(pathname: string) {
  if (pathname === '/' || pathname === '/signin') return <Home />;
  if (pathname === '/accounts') return <Accounts />;
  if (pathname === '/people') return <People />;
  if (pathname === '/settings') return <Settings />;
  if (pathname === '/live') return <Live />;
  if (pathname === '/movies') return <Movies />;
  if (pathname === '/series') return <Series />;
  if (pathname === '/free/playlist' || pathname === '/free/usa') {
    return (
      <Suspense fallback={<Loading label="Loading the playlist" />}>
        <UsaPlaylistTest />
      </Suspense>
    );
  }
  if (pathname === '/free') return <FreeTv />;

  const countries = matchPath('/accounts/:id/countries', pathname);
  if (countries) return <CountryPicker accountId={countries.id} />;

  const movie = matchPath('/movies/:accountId/:id', pathname);
  if (movie && isNumeric(movie.id)) return <MovieDetail accountId={movie.accountId} id={Number(movie.id)} />;

  const series = matchPath('/series/:accountId/:id', pathname);
  if (series && isNumeric(series.id)) return <SeriesDetail accountId={series.accountId} id={Number(series.id)} />;

  return (
    <div className="page">
      <div className="wrap stack">
        <h1>Not found</h1>
        <p className="muted">There is nothing at {pathname}.</p>
        <Link className="btn" to="/">
          Back to the library
        </Link>
      </div>
    </div>
  );
}

function isNumeric(value: string): boolean {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
}
